import React, { useState, useEffect } from 'react';
import { t } from '../i18n';

const WC = { 0:'☀️ Clear Sky', 1:'🌤️ Mainly Clear', 2:'⛅ Partly Cloudy', 3:'☁️ Overcast', 45:'🌫️ Foggy', 48:'🌫️ Icy Fog', 51:'🌦️ Light Drizzle', 53:'🌧️ Drizzle', 55:'🌧️ Heavy Drizzle', 61:'🌧️ Light Rain', 63:'🌧️ Rain', 65:'🌧️ Heavy Rain', 71:'❄️ Light Snow', 73:'❄️ Snow', 75:'❄️ Heavy Snow', 80:'🌦️ Showers', 81:'🌧️ Heavy Showers', 95:'⛈️ Thunderstorm', 96:'⛈️ Thunderstorm & Hail', 99:'⛈️ Heavy Thunderstorm' };
const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function Weather() {
  const [current, setCurrent] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [location, setLocation] = useState('Lahore, Pakistan');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get location from IP
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(d => {
        const lat = d.latitude || 31.5204;
        const lon = d.longitude || 74.3587;
        setLocation(`${d.city || 'Lahore'}, ${d.country_name || 'Pakistan'}`);
        fetchWeather(lat, lon);
      })
      .catch(() => fetchWeather(31.5204, 74.3587));
  }, []);

  const fetchWeather = async (lat, lon) => {
    try {
      const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&hourly=temperature_2m,weathercode,windspeed_10m,relativehumidity_2m&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum,windspeed_10m_max&timezone=auto&forecast_days=7`);
      const data = await res.json();
      
      const cw = data.current_weather;
      setCurrent({
        temp: Math.round(cw.temperature),
        code: cw.weathercode,
        wind: Math.round(cw.windspeed),
        desc: WC[cw.weathercode] || '🌡️ Unknown',
      });

      // 7 day forecast
      const daily = data.daily;
      const days = daily.time.map((t, i) => {
        const d = new Date(t);
        return {
          day: DAYS[d.getDay()],
          date: `${d.getDate()} ${MONTHS[d.getMonth()]}`,
          code: daily.weathercode[i],
          desc: WC[daily.weathercode[i]] || '',
          max: Math.round(daily.temperature_2m_max[i]),
          min: Math.round(daily.temperature_2m_min[i]),
          rain: daily.precipitation_sum[i],
          wind: Math.round(daily.windspeed_10m_max[i]),
        };
      });
      setForecast(days);

      // Hourly (next 24h)
      const now = new Date();
      const currentHour = now.getHours();
      const todayStr = now.toISOString().split('T')[0];
      const hours = [];
      data.hourly.time.forEach((t, i) => {
        if (t.startsWith(todayStr) && parseInt(t.split('T')[1]) >= currentHour && hours.length < 8) {
          hours.push({
            time: t.split('T')[1].substring(0,5),
            temp: Math.round(data.hourly.temperature_2m[i]),
            code: data.hourly.weathercode[i],
            humidity: data.hourly.relativehumidity_2m[i],
          });
        }
      });
      setHourly(hours);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const getEmoji = (code) => {
    const desc = WC[code] || '';
    return desc.split(' ')[0] || '🌡️';
  };

  if (loading) return (
    <div style={S.container}>
      <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
        <div style={{ fontSize: '48px' }}>🌍</div>
        <p>{t('wt_loading')}</p>
      </div>
    </div>
  );

  return (
    <div style={S.container}>
      {/* Hero */}
      <div style={S.hero}>
        <div style={S.location}>📍 {location}</div>
        <div style={S.bigTemp}>{current?.temp}°C</div>
        <div style={S.bigDesc}>{current?.desc}</div>
        <div style={S.currentStats}>
          <div style={S.stat}>💨 {current?.wind} km/h</div>
          <div style={S.stat}>📅 {new Date().toLocaleDateString('en-US', { weekday:'long', day:'numeric', month:'short' })}</div>
        </div>
      </div>

      {/* Hourly */}
      {hourly.length > 0 && (
        <div style={S.section}>
          <h3 style={S.sectionTitle}>{t('wt_hourly')}</h3>
          <div style={S.hourlyRow}>
            {hourly.map((h, i) => (
              <div key={i} style={S.hourCard}>
                <div style={{ fontSize: '11px', color: '#999' }}>{h.time}</div>
                <div style={{ fontSize: '20px' }}>{getEmoji(h.code)}</div>
                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{h.temp}°</div>
                <div style={{ fontSize: '10px', color: '#6200ea' }}>💧{h.humidity}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 7 Day Forecast */}
      <div style={S.section}>
        <h3 style={S.sectionTitle}>{t('wt_7day')}</h3>
        {forecast.map((d, i) => (
          <div key={i} style={S.dayCard}>
            <div style={S.dayLeft}>
              <div style={{ fontWeight: 'bold', color: '#333', fontSize: '14px' }}>{i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.day}</div>
              <div style={{ color: '#999', fontSize: '11px' }}>{d.date}</div>
            </div>
            <div style={{ fontSize: '22px' }}>{getEmoji(d.code)}</div>
            <div style={S.dayRight}>
              <div style={{ color: '#999', fontSize: '11px' }}>{d.desc.split(' ').slice(1).join(' ')}</div>
              <div style={{ fontSize: '11px', color: '#6200ea' }}>💧 {d.rain}mm · 💨 {d.wind}km/h</div>
            </div>
            <div style={S.temps}>
              <span style={{ color: '#ef4444', fontWeight: 'bold' }}>{d.max}°</span>
              <span style={{ color: '#999', fontSize: '12px' }}> / {d.min}°</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const S = {
  container: { maxWidth: '700px', margin: '0 auto', paddingBottom: '30px' },
  hero: { background: 'linear-gradient(135deg, #1a1a2e, #16213e, #0f3460)', padding: '30px 20px', textAlign: 'center', color: 'white' },
  location: { fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '8px' },
  bigTemp: { fontSize: '72px', fontWeight: '900', lineHeight: 1 },
  bigDesc: { fontSize: '18px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' },
  currentStats: { display: 'flex', gap: '20px', justifyContent: 'center', marginTop: '16px' },
  stat: { color: 'rgba(255,255,255,0.7)', fontSize: '13px' },
  section: { padding: '16px 20px' },
  sectionTitle: { color: '#6200ea', marginBottom: '12px', fontSize: '16px' },
  hourlyRow: { display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' },
  hourCard: { backgroundColor: 'white', borderRadius: '12px', padding: '10px 12px', textAlign: 'center', minWidth: '60px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', flexShrink: 0 },
  dayCard: { display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: 'white', borderRadius: '12px', padding: '12px 16px', marginBottom: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' },
  dayLeft: { flex: 1 },
  dayRight: { flex: 1, textAlign: 'right' },
  temps: { minWidth: '60px', textAlign: 'right', fontSize: '15px' },
};
